import {
	collection,
	doc,
	Firestore,
	getDoc,
	getDocs,
	orderBy,
	query,
	setDoc,
	updateDoc,
} from "firebase/firestore";
import {
	AppUserProfile,
	EMPTY_PERMISSIONS,
	SUPER_ADMIN_PERMISSIONS,
	UserPermissions,
} from "../types/userPermissions";

const COLLECTION = "appUsers";

const nowIso = () => new Date().toISOString();

const stripUndefined = <T extends Record<string, unknown>>(data: T): T =>
	Object.fromEntries(
		Object.entries(data).filter(([, value]) => value !== undefined)
	) as T;

export const normalizePermissions = (
	permissions?: Partial<UserPermissions>
): UserPermissions => ({
	projects: {
		view: Boolean(permissions?.projects?.view),
		edit: Boolean(permissions?.projects?.edit),
		delete: Boolean(permissions?.projects?.delete),
	},
	cronogramas: {
		view: Boolean(permissions?.cronogramas?.view),
		edit: Boolean(permissions?.cronogramas?.edit),
		delete: Boolean(permissions?.cronogramas?.delete),
	},
	users: {
		view: Boolean(permissions?.users?.view),
		edit: Boolean(permissions?.users?.edit),
		delete: Boolean(permissions?.users?.delete),
	},
});

export const fetchUserProfile = async (
	firestore: Firestore,
	uid: string
): Promise<AppUserProfile | null> => {
	const snapshot = await getDoc(doc(firestore, COLLECTION, uid));
	if (!snapshot.exists()) {
		return null;
	}
	const data = snapshot.data() as AppUserProfile;
	return {
		...data,
		uid,
		permissions: normalizePermissions(data.permissions),
	};
};

export const listUserProfiles = async (
	firestore: Firestore
): Promise<AppUserProfile[]> => {
	const snapshot = await getDocs(
		query(collection(firestore, COLLECTION), orderBy("createdAt", "desc"))
	);

	return snapshot.docs.map((docSnap) => {
		const data = docSnap.data() as AppUserProfile;
		return {
			...data,
			uid: docSnap.id,
			permissions: normalizePermissions(data.permissions),
		};
	});
};

export const createUserProfile = async (
	firestore: Firestore,
	uid: string,
	payload: {
		email: string;
		displayName: string;
		isSuperAdmin?: boolean;
		permissions?: UserPermissions;
		createdBy?: string;
	}
): Promise<AppUserProfile> => {
	const now = nowIso();
	const profile = stripUndefined({
		uid,
		email: payload.email,
		displayName: payload.displayName,
		isActive: true,
		isSuperAdmin: Boolean(payload.isSuperAdmin),
		permissions: payload.isSuperAdmin
			? SUPER_ADMIN_PERMISSIONS
			: normalizePermissions(payload.permissions || EMPTY_PERMISSIONS),
		createdAt: now,
		updatedAt: now,
		createdBy: payload.createdBy,
	}) as AppUserProfile;

	await setDoc(doc(firestore, COLLECTION, uid), profile);
	return profile;
};

export const updateUserProfile = async (
	firestore: Firestore,
	uid: string,
	payload: Partial<
		Pick<AppUserProfile, "displayName" | "isActive" | "permissions" | "isSuperAdmin">
	>
): Promise<void> => {
	const updates: Record<string, unknown> = {
		updatedAt: nowIso(),
	};

	if (payload.displayName !== undefined) {
		updates.displayName = payload.displayName;
	}
	if (payload.isActive !== undefined) {
		updates.isActive = payload.isActive;
	}
	if (payload.isSuperAdmin !== undefined) {
		updates.isSuperAdmin = payload.isSuperAdmin;
	}
	if (payload.permissions !== undefined) {
		updates.permissions = normalizePermissions(payload.permissions);
	}

	await updateDoc(doc(firestore, COLLECTION, uid), updates as any);
};

export const ensureSuperAdminProfile = async (
	firestore: Firestore,
	uid: string,
	email: string
): Promise<AppUserProfile> => {
	const existing = await fetchUserProfile(firestore, uid);
	if (existing) {
		return existing;
	}

	return createUserProfile(firestore, uid, {
		email,
		displayName: "Administrador Sabaseba",
		isSuperAdmin: true,
		permissions: SUPER_ADMIN_PERMISSIONS,
	});
};
